import { createElement, parseElement } from 'src/utils/dom-create';
import { toggleHidden } from 'src/utils/dom-toggle';
import { htmlEscape } from 'src/utils/html-escape';
import { Context, Controller } from '@hotwired/stimulus';

export default class SearchBarModalController extends Controller {
  constructor(context: Context) {
    super(context);

    this.activateLast = this.activateLast.bind(this);
    this.activateFirst = this.activateFirst.bind(this);
    this.activatePrevious = this.activatePrevious.bind(this);
    this.activateNext = this.activateNext.bind(this);
    this.ensureUniqueId = this.ensureUniqueId.bind(this);

    this.onPointerDown = this.onPointerDown.bind(this);
  }

  public connect(): void {
    Array.from(
      this.element.querySelectorAll<HTMLButtonElement>(
        'button:not([role="none"])',
      ),
    )
      .map(this.ensureUniqueId)
      .forEach(this.ensureNotFocusable);

    this.element.addEventListener('pointerdown', this.onPointerDown);
    this.active = false;
  }

  private get availableItems(): HTMLButtonElement[] {
    return Array.from(
      this.element.querySelectorAll<HTMLButtonElement>(
        'button:not([role="none"]):not([hidden]):enabled',
      ),
    );
  }

  public get activeItem(): HTMLButtonElement | null {
    const activeId = this.element.getAttribute('aria-activedescendant');
    return this.element.querySelector<HTMLButtonElement>(
      `button:not([role="none"]):not([hidden]):enabled#${activeId}`,
    );
  }

  public get active(): boolean {
    return this.element.getAttribute('hidden') === null;
  }

  public set active(next: boolean) {
    toggleHidden(this.element, !next);
  }

  protected get activeDescendant(): HTMLButtonElement | null {
    const activeId = this.element.getAttribute('aria-activedescendant');
    const current = this.element.querySelector<HTMLButtonElement>(
      `button:not([role="none"])#${activeId}`,
    );
    return current;
  }

  public set activeItem(next: HTMLButtonElement | null) {
    const current = this.activeDescendant;
    if (current) {
      current.classList.remove('--active');
    }

    if (!next || !next.id) {
      this.element.removeAttribute('aria-activedescendant');
      return;
    }

    next.classList.add('--active');
    this.element.setAttribute('aria-activedescendant', next.id);

    next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  public activateNext() {
    const currentItem = this.activeItem;
    if (!currentItem) {
      this.activateFirst();
      return;
    }

    const currentItems = this.availableItems;
    const nextIndex =
      (currentItems.indexOf(currentItem) + 1) % currentItems.length;

    this.activeItem = currentItems[nextIndex];
  }

  public activatePrevious() {
    const currentItem = this.activeItem;
    if (!currentItem) {
      this.activateLast();
      return;
    }

    const currentItems = this.availableItems;
    const prevIndex =
      (currentItems.indexOf(currentItem) - 1 + currentItems.length) %
      currentItems.length;

    this.activeItem = currentItems[prevIndex];
  }

  public activateFirst() {
    this.activeItem = this.availableItems[0];
  }

  public activateLast() {
    const currentItems = this.availableItems;
    this.activeItem = currentItems[currentItems.length - 1];
  }

  private ensureUniqueId<K extends HTMLElement>(element: K): K {
    if (!element.id) {
      element.setAttribute(
        'id',
        `${this.element.id}-item-${Math.random().toString(32).substring(2)}`,
      );
    }

    return element;
  }

  private ensureNotFocusable<K extends HTMLElement>(element: K): K {
    if (element.tabIndex != -1) {
      element.setAttribute('tabindex', '-1');
    }

    return element;
  }

  private onPointerDown(event: Event) {
    if (!('isPrimary' in event) || !event.isPrimary) {
      return;
    }

    // This ensures that click events (mouse or pointer) do not blur the input
    // which is fine because keyboard control is handled differently.
    event.preventDefault();
  }

  protected get listBox(): HTMLElement {
    return this.element.querySelector('[role="listbox"]')!;
  }

  protected clearItems() {
    this.listBox.innerHTML = '';
  }

  protected addGroup(children: Element[] = []) {
    const group = createElement(
      'div',
      { role: 'none', class: 'disclosure-menu__item-group' },
      children,
    );
    this.listBox.append(group);
    return group;
  }

  protected createOption({
    action = 'search-bar--token-value-modal#pick',
    valueParam = 'data-search-bar--token-value-modal-value-param',
    labelParam = 'data-search-bar--token-value-modal-label-param',
    value,
    label,
  }: {
    value: string;
    label?: string | null | Element;
    action?: string;
    valueParam?: string;
    labelParam?: string;
  }): HTMLButtonElement {
    return this.ensureNotFocusable(
      this.ensureUniqueId(
        createElement(
          'button',
          {
            type: 'button',
            class:
              'disclosure-menu__item rounded-xs disabled:opacity-50 disabled:cursor-not-allowed',
            'data-action': action,
            role: 'option',
            [valueParam]: value,
            ...(value ? {} : { disabled: '' }),
            ...(label
              ? {
                  [labelParam]:
                    typeof label === 'string'
                      ? label
                      : `html:${htmlEscape(label.outerHTML)}`,
                }
              : {}),
          },
          parseElement(label, 'span', {
            class: 'truncate',
            'data-computed': false,
          }) ??
            createElement(
              'span',
              { class: 'truncate', 'data-computed': false },
              value,
            ),
        ),
      ),
    );
  }
}
